/**
 * SegmentAssignDialog Component - Dialog pentru asocierea unui medic la un segment
 * 
 * Features:
 * - Select dropdown cu lista medicilor din tenant
 * - Filtrare medici după nume
 * - Afișare specialitate medic (dacă există)
 * - Submit cu validare
 * - Error handling
 * 
 * Props:
 * - open: boolean - dialog deschis/închis
 * - onClose: (success: boolean) => void - callback la închidere
 * - segment: Segment object - segmentul pentru care se face asocierea
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import apiClient from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface Doctor {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  full_name: string;
  role: string;
}

interface Segment {
  id: string;
  body_part_code: string;
  modality: string;
  contrast_status: string;
  status: string;
}

interface SegmentAssignDialogProps {
  open: boolean;
  onClose: (success: boolean) => void;
  segment: Segment;
}

const SegmentAssignDialog: React.FC<SegmentAssignDialogProps> = ({
  open,
  onClose,
  segment
}) => {
  const { user: _user } = useAuth();
  
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Load doctors
  useEffect(() => {
    if (open) {
      loadDoctors();
    }
  }, [open]);
  
  const loadDoctors = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all medics with profiles (includes first_name, last_name, full_name)
      const response = await apiClient.get('/pacs/doctors/search?limit=50');
      setDoctors(response.data.doctors || []);
    } catch (err: any) {
      console.error('Error loading doctors:', err);
      setError(err.response?.data?.detail || 'Failed to load doctors');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAssign = async () => {
    if (!selectedDoctorId) {
      alert('Please select a doctor');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      await apiClient.post(`/segments/${segment.id}/assign`, {
        doctor_id: selectedDoctorId
      });
      
      onClose(true); // Success
    } catch (err: any) {
      console.error('Error assigning doctor:', err);
      setError(err.response?.data?.detail || 'Failed to assign doctor');
      setSubmitting(false);
    }
  };
  
  const handleClose = () => {
    if (!submitting) {
      onClose(false);
    }
  };
  
  // Format segment name for display
  const formatSegmentName = (code: string): string => {
    const parts = code.split('_');
    if (parts.length >= 2) {
      const modality = parts[0];
      const bodyPart = parts.slice(1, -1).join(' ');
      const contrast = parts[parts.length - 1];
      return `${modality} ${bodyPart} (${contrast})`;
    }
    return code;
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign Doctor to Segment
      </DialogTitle>
      
      <DialogContent>
        {/* Segment info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Segment
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {formatSegmentName(segment.body_part_code)}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
            <Chip label={segment.modality} size="small" variant="outlined" />
            <Chip label={segment.contrast_status} size="small" variant="outlined" />
            <Chip label={segment.status} size="small" color={segment.status === 'confirmed' ? 'success' : 'warning'} />
          </Box>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <FormControl fullWidth>
            <InputLabel id="doctor-select-label">Select Doctor</InputLabel>
            <Select
              labelId="doctor-select-label"
              id="doctor-select"
              value={selectedDoctorId}
              label="Select Doctor"
              onChange={(e) => setSelectedDoctorId(e.target.value)}
              disabled={submitting}
            >
              {doctors.length === 0 ? (
                <MenuItem value="" disabled>
                  No doctors available
                </MenuItem>
              ) : (
                doctors.map((doctor) => (
                  <MenuItem key={doctor.id} value={doctor.id}>
                    <Box>
                      <Typography variant="body1">
                        {doctor.full_name || `${doctor.first_name} ${doctor.last_name}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {doctor.email}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedDoctorId || submitting || loading}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {submitting ? 'Assigning...' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SegmentAssignDialog;

